import React, { useState, useEffect, useRef, useCallback } from "react";
import { api, API } from "../api";
import { useApp } from "../context/AppContext";
import { captureUtterance, chime } from "../lib/vad";
import { releaseMic } from "../lib/mic";
import { playVoiceUrl, unlockAudio, primeVoicePlayback } from "../lib/sounds";
import { Button } from "./ui/button";
import { Languages, Mic, MicOff, X, Megaphone, Loader2, Volume2, User, GraduationCap, MapPin, Users } from "lucide-react";
import { toast } from "sonner";
import buddyHead from "../assets/buddy-head.webp";

/**
 * InterpreterMode — Buddy as a live professional interpreter between the
 * installer (his profile language) and the customer (English).
 *
 * Flow: /interpreter/start builds the visit context and Buddy SPEAKS a warm
 * professional introduction (company + team + today's scope). Then a
 * continuous hands-free loop runs: capture ONE utterance (VAD) -> the backend
 * detects WHO spoke by language -> Buddy speaks the translation (or answers /
 * learns vocabulary / ends the session). The mic stays CLOSED while Buddy's
 * TTS is playing so he never hears himself.
 */
export const InterpreterMode = ({ onClose }) => {
  const { user, t, lang } = useApp();
  const [phase, setPhase] = useState("starting"); // starting | choose | listening | recording | thinking | speaking | paused | error
  const [turns, setTurns] = useState([]); // {id, side: installer|customer|buddy, original, translated, kind}
  const [session, setSession] = useState(null); // {context_blob, intro_text, intro_gloss, job_label, company_name}
  const [stops, setStops] = useState(null); // today's jobs when the installer has 2+ stops
  const [helpersQ, setHelpersQ] = useState(null); // {fixed:[...]} while Buddy asks "who is with you today?"
  const closedRef = useRef(false);
  const pausedRef = useRef(false);
  const capRef = useRef(null); // active captureUtterance session
  const audioRef = useRef(null); // active playVoice handle
  const scrollRef = useRef(null);
  const phaseRef = useRef("starting");
  const setPh = (p) => { phaseRef.current = p; setPhase(p); };

  const installerLang = user?.language || "pt";
  // the device's LOCAL date — "today" for the visit (never UTC, which flips
  // to the next day during US evenings)
  const localDate = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns, phase]);

  const speechSeq = useRef(0); // bumps on stopAudio -> an in-flight speak() loop aborts
  const stopAudio = () => {
    speechSeq.current += 1;
    try { audioRef.current?.pause?.(); } catch (e) { /* ignore */ }
    audioRef.current = null;
  };

  const cancelCapture = () => {
    const s = capRef.current;
    capRef.current = null;
    try { s?.cancel(); } catch (e) { /* ignore */ }
    releaseMic();
  };

  const cleanup = useCallback(() => {
    closedRef.current = true;
    cancelCapture();
    stopAudio();
    releaseMic();
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const close = () => {
    cleanup();
    onClose?.();
  };

  // ---- speak text via the STREAMING TTS endpoint. Long texts are spoken in
  // sentence-aligned CHUNKS (same algorithm as the backend pre-warm), so there
  // is NO length limit — a 15-minute speech is fully spoken, block by block ----
  const ttsChunks = (text, limit = 320) => {
    const t = (text || "").trim();
    if (!t) return [];
    if (t.length <= limit) return [t];
    let sentences;
    try {
      sentences = t.split(/(?<=[.!?…])\s+/);
    } catch (e) {
      sentences = [t]; // very old engines without lookbehind
    }
    const chunks = [];
    let cur = "";
    for (const s of sentences) {
      if (cur && cur.length + 1 + s.length > limit) { chunks.push(cur); cur = s; }
      else cur = cur ? `${cur} ${s}` : s;
    }
    if (cur) chunks.push(cur);
    const out = [];
    for (let c of chunks) {
      while (c.length > limit) { out.push(c.slice(0, limit)); c = c.slice(limit); }
      if (c) out.push(c);
    }
    return out;
  };

  // iOS FIX: WKWebView's media player gives up on long CHUNKED mp3 streams
  // (no Content-Length) — the introduction showed as text but was never heard.
  // Each chunk is now fully DOWNLOADED to a local blob before playing (the same
  // pattern as chat voice notes), and the next chunk is prefetched while the
  // current one plays, so speech stays continuous.
  const fetchChunkBlob = async (chunk) => {
    const url = `${API}/interpreter/tts?user_id=${encodeURIComponent(user.id)}&text=${encodeURIComponent(chunk)}`;
    const r = await fetch(url);
    if (!r.ok) throw new Error(`tts http ${r.status}`);
    const b = await r.blob();
    return URL.createObjectURL(b.type && b.type !== "application/octet-stream" ? b : new Blob([b], { type: "audio/mpeg" }));
  };

  const playBlobUrl = (blobUrl) => new Promise((resolve) => {
    let done = false;
    const finish = () => { if (!done) { done = true; try { URL.revokeObjectURL(blobUrl); } catch (e) { /* ignore */ } resolve(); } };
    playVoiceUrl(blobUrl, { onEnd: finish }).then((handle) => {
      if (!handle) { finish(); return; }
      audioRef.current = handle;
    }).catch(finish);
  });

  /** Speaks the text chunk by chunk. Returns false when interrupted (pause,
   *  close or a new speech started via stopAudio) so callers don't re-arm. */
  const speak = async (textToSpeak) => {
    const clean = (textToSpeak || "").trim();
    if (!clean || closedRef.current) return true;
    const my = ++speechSeq.current;
    const alive = () => !closedRef.current && !pausedRef.current && speechSeq.current === my;
    setPh("speaking");
    const chunks = ttsChunks(clean);
    let next = fetchChunkBlob(chunks[0]).catch(() => null);
    for (let i = 0; i < chunks.length; i++) {
      if (!alive()) return false;
      const blobUrl = await next;
      if (i + 1 < chunks.length) next = fetchChunkBlob(chunks[i + 1]).catch(() => null);
      if (!alive()) { if (blobUrl) URL.revokeObjectURL(blobUrl); return false; }
      if (blobUrl) await playBlobUrl(blobUrl);
      else toast.error(t("interp_turn_failed"));
    }
    return alive();
  };

  const historyFor = (list) => list.slice(-10).flatMap((tn) => {
    const out = [];
    if (tn.side !== "buddy" && tn.original) out.push({ speaker: tn.side, text: tn.original });
    if (tn.translated) out.push({ speaker: "buddy", text: tn.translated });
    return out;
  });

  // ---- one capture->turn cycle, then re-arms itself (continuous) ----
  const loopRef = useRef(null);
  const failRef = useRef({ n: 0, ts: 0 }); // fast consecutive failures = mic denied
  const loop = useCallback(async () => {
    if (closedRef.current || pausedRef.current || capRef.current || stopsRef.current || helpersRef.current) return;
    setPh("listening");
    const startedAt = Date.now();
    const cap = captureUtterance({
      waitForSpeechMs: 60000, // stays armed — a home visit has natural silences
      silenceMs: 950,
      maxUtteranceMs: 900000, // NO practical limit — up to 15 min per speech
      armDelayMs: 150,
      onSpeechStart: () => setPh("recording"),
    });
    capRef.current = cap;
    let result = null;
    try { result = await cap.promise; } catch (e) { result = null; }
    if (capRef.current !== cap) return; // cancelled (pause/close)
    capRef.current = null;
    if (closedRef.current || pausedRef.current) return;
    if (!result) {
      // instant failure (mic denied/unavailable) three times in a row -> pause
      if (Date.now() - startedAt < 2000) {
        failRef.current.n += 1;
        if (failRef.current.n >= 3) {
          failRef.current.n = 0;
          pausedRef.current = true;
          setPh("paused");
          toast.error(t("buddy_mic_denied"));
          return;
        }
      } else {
        failRef.current.n = 0;
      }
      loopRef.current?.();
      return;
    }
    failRef.current.n = 0;
    chime("end");
    setPh("thinking");
    try {
      const fd = new FormData();
      fd.append("user_id", user.id);
      fd.append("context_blob", sessRef.current?.context_blob || "{}");
      fd.append("history_json", JSON.stringify(historyFor(turnsRef.current)));
      fd.append("speak", "0"); // audio comes via the streaming TTS endpoint
      fd.append("audio", result.blob, result.filename);
      const { data } = await api.post("/interpreter/turn", fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
      await handleTurn(data);
    } catch (e) {
      if (!closedRef.current) {
        toast.error(t("interp_turn_failed"));
        loopRef.current?.();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
  loopRef.current = loop;

  const turnsRef = useRef(turns);
  turnsRef.current = turns;
  const sessRef = useRef(session);
  sessRef.current = session;
  const stopsRef = useRef(stops);
  stopsRef.current = stops;
  const helpersRef = useRef(helpersQ);
  helpersRef.current = helpersQ;
  const helpersLoopRef = useRef(null);
  const jobIdRef = useRef(""); // the chosen stop (kept for re-intro / force_intro)
  const chooseLoopRef = useRef(null);
  const beginSessionRef = useRef(null);

  const pushTurn = (tn) => setTurns((prev) => [...prev, { id: Date.now() + Math.random(), ...tn }]);

  const handleTurn = async (data) => {
    const { action, original_text, translated_text, speak_text, detected_lang, learned } = data || {};
    if (!action || action === "ignore") { loopRef.current?.(); return; }
    if (action === "intro") {
      // "Buddy, apresenta a gente" — replay (or fetch, if it was skipped) the introduction
      await reIntro();
      return;
    }
    const side = detected_lang && detected_lang !== installerLang ? "customer" : "installer";
    if (action === "learn") {
      pushTurn({ side, original: original_text, translated: speak_text, kind: "learn" });
      toast.success(t("interp_learned"));
    } else if (action === "answer") {
      if (original_text) pushTurn({ side, original: original_text, translated: "", kind: "speech" });
      pushTurn({ side: "buddy", original: speak_text, translated: translated_text, kind: "answer" });
    } else {
      // translate / exit
      pushTurn({ side, original: original_text, translated: speak_text || translated_text, kind: action === "exit" ? "exit" : "speech" });
    }
    if (learned > 0 && action !== "learn") toast.success(t("interp_learned"));
    const ok = await speak(speak_text);
    if (action === "exit") { close(); return; }
    if (!ok) return; // interrupted (re-intro / pause) — the interrupter re-arms the loop
    loopRef.current?.();
  };

  const forceIntroRef = useRef(false); // re-introduce after a helpers check-in triggered by "Reapresentar"
  // ---- start (or restart with a chosen stop) the interpreter session ----
  const beginSession = async (jobId = "", helpers = null) => {
    setPh("starting");
    try {
      const { data } = await api.post("/interpreter/start",
        // installer_lang = the CURRENT page language — the installer ALWAYS
        // receives translations in the language the app is displayed in
        { user_id: user.id, speak: false, job_id: jobId || "", local_date: localDate(), installer_lang: lang,
          helpers_answered: Array.isArray(helpers), helpers_today: helpers || [], force_intro: !!forceIntroRef.current }, { timeout: 90000 });
      forceIntroRef.current = false;
      if (closedRef.current) return;
      if (data.needs_helpers) {
        // company rule: BEFORE the introduction, Buddy asks the installer (privately) who is with him today
        const hq = { fixed: data.fixed_helpers || [] };
        setHelpersQ(hq);
        helpersRef.current = hq;
        pushTurn({ side: "buddy", original: data.question, translated: "", kind: "helpers" });
        await speak(data.question);
        if (!closedRef.current && helpersRef.current) helpersLoopRef.current?.();
        return;
      }
      if (data.needs_job_choice) {
        // 2+ stops today: Buddy asks WHICH one before introducing anyone
        setStops(data.jobs);
        stopsRef.current = data.jobs;
        const q = t("interp_choose_speak").replace("{n}", String(data.jobs.length));
        pushTurn({ side: "buddy", original: q, translated: "", kind: "choose" });
        await speak(q);
        if (!closedRef.current && stopsRef.current) chooseLoopRef.current?.();
        return;
      }
      const sess = {
        context_blob: data.context_blob,
        intro_text: data.intro_text,
        intro_gloss: data.intro_gloss,
        introSkipped: !!data.intro_skipped,
        job_label: data.job_label,
        company_name: data.company_name,
        helpers_today: Array.isArray(helpers) ? helpers : (Array.isArray(data.helpers_today) ? data.helpers_today : sessRef.current?.helpers_today),
      };
      setSession(sess);
      sessRef.current = sess;
      pushTurn({ side: "buddy", original: data.intro_text, translated: data.intro_gloss, kind: data.intro_skipped ? "ready" : "intro" });
      const ok = await speak(data.intro_text);
      if (ok && !closedRef.current) loopRef.current?.();
    } catch (e) {
      if (!closedRef.current) {
        setPh("error");
        toast.error(t("interp_start_failed"));
      }
    }
  };
  beginSessionRef.current = beginSession;

  // ---- the installer picked a stop (by voice or by tapping a card) ----
  const chooseJob = (jobId, heard = "") => {
    if (!stopsRef.current || closedRef.current) return;
    cancelCapture();
    stopAudio();
    setStops(null);
    stopsRef.current = null;
    jobIdRef.current = jobId;
    if (heard) pushTurn({ side: "installer", original: heard, translated: "", kind: "speech" });
    beginSessionRef.current?.(jobId);
  };

  // ---- the installer answered "who is with you today?" (voice, or a quick button) ----
  const answerHelpers = async ({ blob, filename, text }) => {
    if (!helpersRef.current || closedRef.current) return;
    setPh("thinking");
    try {
      const fd = new FormData();
      fd.append("user_id", user.id);
      fd.append("installer_lang", lang);
      if (text) fd.append("text", text);
      if (blob) fd.append("audio", blob, filename);
      const { data } = await api.post("/interpreter/helpers", fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 40000 });
      if (closedRef.current || !helpersRef.current) return;
      if (data.heard) pushTurn({ side: "installer", original: data.heard, translated: "", kind: "speech" });
      if (!data.understood) {
        const retry = t("interp_helpers_retry");
        pushTurn({ side: "buddy", original: retry, translated: "", kind: "helpers" });
        await speak(retry);
        if (!closedRef.current && helpersRef.current) helpersLoopRef.current?.();
        return;
      }
      if (data.confirm) {
        pushTurn({ side: "buddy", original: data.confirm, translated: "", kind: "helpers" });
        await speak(data.confirm);
      }
      if (closedRef.current) return;
      setHelpersQ(null);
      helpersRef.current = null;
      beginSessionRef.current?.(jobIdRef.current || "", data.helpers_today || []);
    } catch (e) {
      if (!closedRef.current && helpersRef.current) helpersLoopRef.current?.();
    }
  };

  const helpersLoop = async () => {
    if (closedRef.current || pausedRef.current || capRef.current || !helpersRef.current) return;
    setPh("listening");
    const startedAt = Date.now();
    const cap = captureUtterance({ waitForSpeechMs: 60000, silenceMs: 900, maxUtteranceMs: 20000, armDelayMs: 150,
      onSpeechStart: () => setPh("recording") });
    capRef.current = cap;
    let result = null;
    try { result = await cap.promise; } catch (e) { result = null; }
    if (capRef.current !== cap) return;
    capRef.current = null;
    if (closedRef.current || pausedRef.current || !helpersRef.current) return;
    if (!result) {
      if (Date.now() - startedAt < 2000) {
        failRef.current.n += 1;
        if (failRef.current.n >= 3) { failRef.current.n = 0; pausedRef.current = true; setPh("paused"); toast.error(t("buddy_mic_denied")); return; }
      } else failRef.current.n = 0;
      helpersLoopRef.current?.();
      return;
    }
    failRef.current.n = 0;
    chime("end");
    await answerHelpers({ blob: result.blob, filename: result.filename });
  };
  helpersLoopRef.current = helpersLoop;

  // quick buttons under the question (noisy driveway): "alone" / "only the regular ones"
  const quickHelpers = (text) => {
    if (!helpersRef.current) return;
    cancelCapture();
    stopAudio();
    answerHelpers({ text });
  };

  // ---- voice loop for "which stop are you at?" (before the intro) ----
  const chooseLoop = async () => {
    if (closedRef.current || pausedRef.current || capRef.current || !stopsRef.current) return;
    setPh("listening");
    const startedAt = Date.now();
    const cap = captureUtterance({
      waitForSpeechMs: 60000,
      silenceMs: 900,
      maxUtteranceMs: 12000,
      armDelayMs: 150,
      onSpeechStart: () => setPh("recording"),
    });
    capRef.current = cap;
    let result = null;
    try { result = await cap.promise; } catch (e) { result = null; }
    if (capRef.current !== cap) return; // cancelled (tap/pause/close)
    capRef.current = null;
    if (closedRef.current || pausedRef.current || !stopsRef.current) return;
    if (!result) {
      // instant failure (mic denied/unavailable) three times in a row -> pause
      if (Date.now() - startedAt < 2000) {
        failRef.current.n += 1;
        if (failRef.current.n >= 3) {
          failRef.current.n = 0;
          pausedRef.current = true;
          setPh("paused");
          toast.error(t("buddy_mic_denied"));
          return; // the stop cards stay tappable
        }
      } else {
        failRef.current.n = 0;
      }
      chooseLoopRef.current?.();
      return;
    }
    failRef.current.n = 0;
    chime("end");
    setPh("thinking");
    try {
      const fd = new FormData();
      fd.append("user_id", user.id);
      fd.append("jobs_json", JSON.stringify(stopsRef.current));
      fd.append("audio", result.blob, result.filename);
      const { data } = await api.post("/interpreter/choose_stop", fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 30000 });
      if (closedRef.current || !stopsRef.current) return; // card tapped meanwhile
      if (data.job_id) { chooseJob(data.job_id, data.heard); return; }
      if (data.heard) pushTurn({ side: "installer", original: data.heard, translated: "", kind: "speech" });
      const retry = t("interp_choose_retry");
      pushTurn({ side: "buddy", original: retry, translated: "", kind: "choose" });
      await speak(retry);
      if (!closedRef.current && stopsRef.current) chooseLoopRef.current?.();
    } catch (e) {
      if (!closedRef.current && stopsRef.current) chooseLoopRef.current?.();
    }
  };
  chooseLoopRef.current = chooseLoop;

  // ---- start the session (intro, or the stop question when 2+ stops today) ----
  useEffect(() => {
    // React StrictMode (dev) mounts -> unmounts -> remounts: schedule the start
    // on a micro-delay so the aborted first pass never runs (no double intro).
    closedRef.current = false;
    pausedRef.current = false;
    try { unlockAudio(); } catch (e) { /* ignore */ }
    const tm = setTimeout(() => { if (!closedRef.current) beginSessionRef.current?.(""); }, 60);
    return () => clearTimeout(tm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const togglePause = () => {
    // any tap is a fresh user gesture — re-prime streamed playback
    try { primeVoicePlayback(); } catch (e) { /* ignore */ }
    if (pausedRef.current) {
      pausedRef.current = false;
      (helpersRef.current ? helpersLoopRef : stopsRef.current ? chooseLoopRef : loopRef).current?.();
    } else {
      pausedRef.current = true;
      cancelCapture();
      stopAudio();
      setPh("paused");
    }
  };

  const reIntro = async () => {
    if (phaseRef.current === "starting" || phaseRef.current === "thinking") return;
    try { primeVoicePlayback(); } catch (e) { /* ignore */ }
    stopAudio(); // tapped while Buddy talks ("I'm ready...") -> cut him and re-introduce
    cancelCapture();
    pausedRef.current = false; // re-introducing always resumes the session
    // If the intro was skipped on reopen (already introduced), fetch the full
    // introduction on demand (force_intro) — voice: "Buddy, apresenta a gente".
    if (!sessRef.current?.intro_text || sessRef.current?.introSkipped) {
      setPh("thinking");
      try {
        const known = sessRef.current?.helpers_today;
        const { data } = await api.post("/interpreter/start",
          { user_id: user.id, speak: false, force_intro: true, job_id: jobIdRef.current || "", local_date: localDate(), installer_lang: lang,
            helpers_answered: Array.isArray(known), helpers_today: known || [] }, { timeout: 90000 });
        if (closedRef.current) return;
        if (data.needs_helpers) {
          // helpers check-in is on and was never answered in this session: ask first, then introduce (forced)
          forceIntroRef.current = true;
          const hq = { fixed: data.fixed_helpers || [] };
          setHelpersQ(hq);
          helpersRef.current = hq;
          pushTurn({ side: "buddy", original: data.question, translated: "", kind: "helpers" });
          await speak(data.question);
          if (!closedRef.current && helpersRef.current) helpersLoopRef.current?.();
          return;
        }
        if (!data.intro_text) throw new Error("empty_intro");
        const sess = {
          ...sessRef.current,
          context_blob: data.context_blob,
          intro_text: data.intro_text,
          intro_gloss: data.intro_gloss,
          introSkipped: false,
        };
        setSession(sess);
        sessRef.current = sess;
      } catch (e) {
        toast.error(t("interp_start_failed"));
        if (!pausedRef.current && !closedRef.current) loopRef.current?.();
        return;
      }
    }
    pushTurn({ side: "buddy", original: sessRef.current.intro_text, translated: sessRef.current.intro_gloss, kind: "intro" });
    const ok = await speak(sessRef.current.intro_text);
    if (ok && !pausedRef.current && !closedRef.current) loopRef.current?.();
  };

  const statusText = helpersQ && (phase === "listening" || phase === "recording")
    ? t("interp_helpers_status")
    : stops && (phase === "listening" || phase === "recording")
    ? t("interp_choose_status")
    : {
      starting: t("interp_starting"),
    listening: t("interp_listening"),
    recording: t("interp_recording"),
    thinking: t("interp_thinking"),
    speaking: t("interp_speaking"),
    paused: t("interp_paused"),
    error: t("interp_start_failed"),
  }[phase];
  const sideLabel = { installer: t("interp_installer"), customer: t("interp_customer"), buddy: "Buddy" };

  return (
    <div className="fixed inset-0 z-[70] bg-background flex flex-col" data-testid="interpreter-overlay"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}>
      {/* header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b bg-card">
        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <Languages className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-bold text-sm leading-tight" data-testid="interpreter-title">{t("interp_title")}</div>
          <div className="text-[11px] text-muted-foreground truncate" data-testid="interpreter-job-label">
            {session?.job_label || session?.company_name || t("interp_subtitle")}
          </div>
          {session && !session.job_label && (
            <div className="text-[10px] text-amber-600 dark:text-amber-400 truncate" data-testid="interpreter-no-job">{t("interp_no_job")}</div>
          )}
        </div>
        <Button size="sm" variant="destructive" onClick={close} data-testid="interpreter-end-btn">
          <X className="h-4 w-4 mr-1" /> {t("interp_end")}
        </Button>
      </div>

      {/* transcript */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-3" data-testid="interpreter-transcript">
        {phase === "starting" && turns.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center gap-3 text-center px-6">
            <img src={buddyHead} alt="Buddy" className="h-20 w-20 object-contain animate-bounce" />
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">{t("interp_preparing")}</p>
          </div>
        )}
        {turns.map((tn) => (
          tn.side === "buddy" ? (
            <div key={tn.id} className="mx-auto max-w-[92%] rounded-2xl border-2 border-primary/25 bg-primary/5 px-3 py-2"
              data-testid="interpreter-buddy-bubble">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-primary mb-1">
                <img src={buddyHead} alt="" className="h-4 w-4 object-contain" />
                {tn.kind === "intro" ? t("interp_intro_label") : "Buddy"}
              </div>
              <p className="text-sm leading-snug">{tn.original}</p>
              {tn.translated ? (
                <p className="text-xs text-muted-foreground mt-1.5 pt-1.5 border-t border-primary/10 leading-snug">{tn.translated}</p>
              ) : null}
            </div>
          ) : (
            <div key={tn.id} className={`max-w-[85%] ${tn.side === "installer" ? "ml-auto" : "mr-auto"}`}
              data-testid={`interpreter-${tn.side}-bubble`}>
              <div className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide mb-0.5 text-muted-foreground ${tn.side === "installer" ? "justify-end" : ""}`}>
                {tn.kind === "learn" ? <GraduationCap className="h-3 w-3" /> : <User className="h-3 w-3" />}
                {sideLabel[tn.side]}
              </div>
              <div className={`rounded-2xl px-3 py-2 border ${tn.side === "installer"
                ? "bg-primary text-primary-foreground border-primary rounded-br-sm"
                : "bg-card border-border rounded-bl-sm"}`}>
                <p className="text-sm leading-snug">{tn.original}</p>
                {tn.translated ? (
                  <p className={`text-xs mt-1.5 pt-1.5 border-t leading-snug ${tn.side === "installer"
                    ? "border-primary-foreground/20 text-primary-foreground/80"
                    : "border-border text-muted-foreground"}`}>
                    {tn.translated}
                  </p>
                ) : null}
              </div>
            </div>
          )
        ))}
      </div>

      {/* stop choice — 2+ stops today: speak OR tap */}
      {stops && (
        <div className="border-t bg-card/95 backdrop-blur-md px-4 pt-3 pb-2 space-y-2 max-h-[45%] overflow-y-auto"
          data-testid="interpreter-stop-choice">
          <div>
            <p className="text-sm font-bold flex items-center gap-1.5">
              <MapPin className="h-4 w-4 text-primary" /> {t("interp_choose_title")}
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">{t("interp_choose_sub")}</p>
          </div>
          {stops.map((j, i) => (
            <button key={j.id} type="button"
              onClick={() => { try { primeVoicePlayback(); } catch (e) { /* ignore */ } chooseJob(j.id); }}
              className="w-full text-left rounded-xl border-2 border-primary/20 bg-background px-3 py-2.5 hover:border-primary focus-visible:border-primary active:scale-[0.99] transition-transform"
              data-testid={`interpreter-stop-option-${i}`}>
              <div className="flex items-center gap-2.5">
                <span className="h-7 w-7 rounded-full bg-primary/10 text-primary text-sm font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{j.customer_name || (j.job_number ? `#${j.job_number}` : t("interp_customer"))}</p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {[j.job_number ? `#${j.job_number}` : "", j.city].filter(Boolean).join(" · ")}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* helpers check-in — speak OR tap a quick answer */}
      {helpersQ && (
        <div className="border-t bg-card/95 backdrop-blur-md px-4 pt-3 pb-2 space-y-2" data-testid="interpreter-helpers-choice">
          <p className="text-sm font-bold flex items-center gap-1.5">
            <Users className="h-4 w-4 text-primary" /> {t("interp_helpers_title")}
          </p>
          <p className="text-[11px] text-muted-foreground">
            {helpersQ.fixed?.length ? `${t("interp_helpers_fixed")}: ${helpersQ.fixed.join(", ")}` : t("interp_helpers_none_saved")}
          </p>
          <div className="flex gap-2 flex-wrap">
            <button type="button" data-testid="interpreter-helpers-alone"
              onClick={() => { try { primeVoicePlayback(); } catch (e) { /* ignore */ } quickHelpers(t("interp_helpers_alone_say")); }}
              className="rounded-full border-2 border-primary/20 bg-background px-3 py-1.5 text-xs font-semibold hover:border-primary active:scale-[0.98]">
              {t("interp_helpers_alone")}
            </button>
            {helpersQ.fixed?.length > 0 && (
              <button type="button" data-testid="interpreter-helpers-fixed"
                onClick={() => { try { primeVoicePlayback(); } catch (e) { /* ignore */ } quickHelpers(t("interp_helpers_fixed_say")); }}
                className="rounded-full border-2 border-primary/20 bg-background px-3 py-1.5 text-xs font-semibold hover:border-primary active:scale-[0.98]">
                {t("interp_helpers_only_fixed")}
              </button>
            )}
          </div>
        </div>
      )}

      {/* status + controls */}
      <div className="border-t bg-card px-4 pt-3 pb-4 space-y-3">
        <div className="flex items-center justify-center gap-2" data-testid="interpreter-status">
          {phase === "listening" && <span className="relative flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-60" /><span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" /></span>}
          {phase === "recording" && <Mic className="h-4 w-4 text-destructive animate-pulse" />}
          {phase === "thinking" && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
          {phase === "speaking" && <Volume2 className="h-4 w-4 text-primary animate-pulse" />}
          {phase === "paused" && <MicOff className="h-4 w-4 text-muted-foreground" />}
          <span className="text-xs font-medium text-muted-foreground">{statusText}</span>
        </div>
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" onClick={reIntro} disabled={!session || phase === "starting"}
            data-testid="interpreter-reintro-btn">
            <Megaphone className="h-4 w-4 mr-1.5" /> {t("interp_reintro")}
          </Button>
          <Button variant={phase === "paused" ? "default" : "outline"} size="sm" onClick={togglePause}
            disabled={phase === "starting" || phase === "error"} data-testid="interpreter-pause-btn">
            {phase === "paused"
              ? <><Mic className="h-4 w-4 mr-1.5" /> {t("interp_resume")}</>
              : <><MicOff className="h-4 w-4 mr-1.5" /> {t("interp_pause")}</>}
          </Button>
        </div>
        <p className="text-center text-[10px] text-muted-foreground leading-tight">{t("interp_hint")}</p>
      </div>
    </div>
  );
};
